import {
    ActiveValueType,
    ContextOption,
    GetPatientOverviewSeveritiesResponsePayload,
    getSeverityColor,
    getSeverityScore,
    getTrendAnalysisColor,
    getWorseColor,
    isActiveValueType,
    MathAPI,
    ValueType,
    ValueTypeValueConverters
} from "pokal_api"
import Card from "./Card"
import ContextChips from "./analysis/shared/ContextChips"
import { useAppDispatch, useAppSelector } from "../redux/store/hooks"
import {
    selectDashboardDataSeverities,
    selectDashboardValueType
} from "../redux/ProfessionalDashboard"
import notNull from "../util/notNull"
import SmallestHeadingComponent from "./SmallestHeadingComponent"
import { valueTypeNames } from "../util/valueTypeNames"
import PokalColorConverter from "../util/PokalColorConverter"
import trendAnalysisToString from "../util/trendAnalysisToString"
import ActiveValueTypeLatestIndicator from "./ActiveValueTypeLatestIndicator"
import { useNavigate } from "react-router-dom"
import {
    NAVIGATION_VIEW,
    NavigationPaths
} from "../navigation/NavigationController"
import { useEffect, useState } from "react"
import mockPatientSeverities from "../onboarding/mock/mockPatientSeverities"
import {
    hidePatientOverviewValueTypeBox,
    mockSeveritiesUsed,
    selectOnboardingIsRunningState,
    selectOnboardingStep
} from "../redux/Onboarding"

interface ValueTypeOverviewComponentProps {
    focused: ValueType
    secondary?: ValueType
    compact?: boolean
    activeContextOptions?: ContextOption[]
    onContextChange?: (options: ContextOption[]) => void
}

function ValueTypeOverviewComponent(props: ValueTypeOverviewComponentProps) {
    const [severities, setSeverities] =
        useState<GetPatientOverviewSeveritiesResponsePayload | null>(null)

    const fetchedSeverities = useAppSelector(selectDashboardDataSeverities)
    const mockSeverities = mockPatientSeverities()

    const running = useAppSelector(selectOnboardingIsRunningState)
    const step = useAppSelector(selectOnboardingStep)

    useEffect(() => {
        if (mockSeveritiesUsed(running, step)) {
            setSeverities(mockSeverities)
        } else {
            setSeverities(fetchedSeverities)
        }
    }, [running, step, fetchedSeverities])

    function getColor() {
        if (notNull(severities)) {
            if (props.secondary) {
                return getWorseColor(
                    getTrendAnalysisColor(severities.relative[props.secondary]),
                    getTrendAnalysisColor(severities.relative[props.focused])
                )
            } else if (
                isActiveValueType(props.focused) &&
                severities.last[props.focused] !== null
            ) {
                return getWorseColor(
                    getTrendAnalysisColor(severities.relative[props.focused]),
                    getSeverityColor(
                        getSeverityScore(
                            severities.last[props.focused]!,
                            props.focused as ActiveValueType
                        ),
                        props.focused as ActiveValueType
                    )
                )
            } else {
                return getTrendAnalysisColor(severities.relative[props.focused])
            }
        }
        return "neutral"
    }

    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    function onClick() {
        dispatch(selectDashboardValueType(props.focused))
        navigate(NavigationPaths[NAVIGATION_VIEW.PATIENT_DETAIL_VIEW].path)
    }

    function onClickSecondary() {
        if (props.secondary) {
            dispatch(selectDashboardValueType(props.secondary))
            navigate(NavigationPaths[NAVIGATION_VIEW.PATIENT_DETAIL_VIEW].path)
        }
    }

    return notNull(severities) ? (
        <Card
            blur={hidePatientOverviewValueTypeBox(running, step, props.focused)}
            onClick={props.secondary ? undefined : onClick}
            width={"100%"}
            className={props.onContextChange ? "h-full" : undefined}
            outline={PokalColorConverter.toTopBorder(getColor())}>
            <div className={`flex flex-col ${props.onContextChange ? "h-full" : ""} ${props.compact ? "gap-3" : "gap-14"}`}>
                <div className={`flex flex-col flex-1 ${props.compact ? "space-y-2" : "space-y-4"}`}>
                    <SmallestHeadingComponent
                        onClick={props.secondary ? onClick : undefined}>
                        {valueTypeNames[props.focused]}
                    </SmallestHeadingComponent>
                    <div className={"flex space-x-1 items-end"}>
                        <div className={"w-20"}>Aktuell</div>
                        <div className={`font-bold ${props.compact ? "text-2xl" : "text-[40px]"}`}>
                            {severities.last[props.focused]
                                ? ValueTypeValueConverters[props.focused](
                                      MathAPI.roundToOneDecimal(
                                          severities.last[props.focused]!.value
                                      )
                                  )
                                : "N/A"}
                        </div>
                    </div>
                    {isActiveValueType(props.focused) &&
                        notNull(severities.last[props.focused]) && (
                            <ActiveValueTypeLatestIndicator
                                valueType={props.focused as ActiveValueType}
                                value={severities.last[props.focused]?.value}
                            />
                        )}
                    {/* Trend nur anzeigen, wenn nicht SFQ */}
                    {props.focused !== ValueType.SFQUESTIONNAIRE && (
                        <div className={"flex space-x-1 items-end"}>
                            <div className={"w-20"}>Trend</div>
                            <div
                                className={PokalColorConverter.toBadge(
                                    getTrendAnalysisColor(
                                        severities.relative[props.focused]
                                    )
                                )}>
                                {trendAnalysisToString(
                                    severities.relative[props.focused]
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {props.secondary !== undefined && (
                    <div className={`flex flex-col ${props.compact ? "space-y-2" : "space-y-4"}`}>
                        <div className={"flex space-x-1"}>
                            <div>Verwandte Datenkategorie</div>
                            <div
                                onClick={onClickSecondary}
                                className={
                                    "font-bold cursor-pointer hover:underline"
                                }>
                                {valueTypeNames[props.secondary]}
                            </div>
                        </div>
                        <div className={"flex space-x-1"}>
                            <div className={"w-20"}>Trend</div>
                            <div
                                className={PokalColorConverter.toBadge(
                                    getTrendAnalysisColor(
                                        severities.relative[props.secondary]
                                    )
                                )}>
                                {trendAnalysisToString(
                                    severities.relative[props.secondary]
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {props.onContextChange && (
                    <div className="border-t border-dashed border-gray-200 pt-2 flex-shrink-0">
                        <span className="text-xs text-gray-400 block mb-1">Ich habe Fragen zu:</span>
                        <ContextChips
                            active={props.activeContextOptions ?? []}
                            onChange={props.onContextChange}
                        />
                    </div>
                )}
            </div>
        </Card>
    ) : (
        <></>
    )
}

export default ValueTypeOverviewComponent
